import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getProductVariants,
  getVariantFormValue,
  isVariantAvailable,
} from "../../layouts/product-recommendation/utils/product-variant-utils";
import CartDrawerVariantSelect from "./CartDrawerVariantSelect";
import {
  ensureRewardBarRuntimeContext,
  getRewardTierSelectableProducts,
  getTiersForFreeGiftPicker,
  getTiersNeedingGiftSelection,
  getTiersAllowingGiftChange,
  grantSelectedRewardGifts,
  getLineRewardTierId,
} from "../../utils/reward-bar-runtime";
import { extractShopifyNumericId } from "../../../shared/checkout-tracking.js";
import {
  DEFAULT_FREE_GIFT_PICKER_CONFIRM_LABEL,
  DEFAULT_FREE_GIFT_PICKER_CONTINUE_LABEL,
  DEFAULT_FREE_GIFT_PICKER_COUNTER_LABEL,
  DEFAULT_FREE_GIFT_PICKER_FREE_LABEL,
  DEFAULT_FREE_GIFT_PICKER_KICKER,
  DEFAULT_FREE_GIFT_PICKER_SUBTITLE,
  DEFAULT_FREE_GIFT_PICKER_TITLE,
  isSpecificProductsRewardMode,
} from "../../../shared/reward-bar.js";

function resolvePickerCopy(rewardSetting = {}) {
  const text = (value, fallback) => {
    const raw = String(value ?? "").trim();
    return raw || fallback;
  };
  return {
    kicker: text(rewardSetting.freeGiftPickerKicker, DEFAULT_FREE_GIFT_PICKER_KICKER),
    title: text(rewardSetting.freeGiftPickerTitle, DEFAULT_FREE_GIFT_PICKER_TITLE),
    subtitle: text(
      rewardSetting.freeGiftPickerSubtitle,
      DEFAULT_FREE_GIFT_PICKER_SUBTITLE
    ),
    confirmLabel: text(
      rewardSetting.freeGiftPickerConfirmLabel,
      DEFAULT_FREE_GIFT_PICKER_CONFIRM_LABEL
    ),
    continueLabel: text(
      rewardSetting.freeGiftPickerContinueLabel,
      DEFAULT_FREE_GIFT_PICKER_CONTINUE_LABEL
    ),
    counterLabel: text(
      rewardSetting.freeGiftPickerCounterLabel,
      DEFAULT_FREE_GIFT_PICKER_COUNTER_LABEL
    ),
    freeLabel: text(
      rewardSetting.freeGiftPickerFreeLabel,
      DEFAULT_FREE_GIFT_PICKER_FREE_LABEL
    ),
  };
}

function applyCountTemplate(template, count) {
  return String(template || "").replace(/\{count\}/gi, String(count));
}

function applyCounterTemplate(template, selected, limit) {
  return String(template || "")
    .replace(/\{selected\}/gi, String(selected))
    .replace(/\{limit\}/gi, String(limit));
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function GiftHeaderIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M20 12v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M3 9h18v3H3V9Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M12 22V9M12 9c0-2.2-1.2-4-3.2-4S6 7.2 6 8.5 7.5 10 9 10h3m0-1c0-2.2 1.2-4 3.2-4S18 7.2 18 8.5 16.5 10 15 10h-3"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function productImage(product) {
  return (
    product?.featured_image ||
    product?.image?.src ||
    product?.images?.[0]?.src ||
    product?.images?.[0] ||
    product?.variants?.[0]?.featured_image?.src ||
    ""
  );
}

function productPriceCents(product, variantId) {
  const variants = getProductVariants(product);
  const variant =
    variants.find((v) => getVariantFormValue(v) === String(variantId)) ||
    variants[0];
  const raw =
    variant?.price ??
    product?.price ??
    product?.variants?.[0]?.price ??
    0;
  const num = Number(raw);
  if (!Number.isFinite(num)) return 0;
  // Admin/catalog prices are often dollars; cart.js uses cents.
  if (num > 0 && num < 1000 && String(raw).includes(".")) {
    return Math.round(num * 100);
  }
  if (num > 0 && num < 500 && !String(raw).includes(".")) {
    // Ambiguous small integers — treat as dollars when product looks catalog-shaped.
    if (product?.variants) return Math.round(num * 100);
  }
  return Math.round(num);
}

function defaultVariantId(product) {
  const variants = getProductVariants(product);
  const available = variants.find((v) => isVariantAvailable(v));
  const pick = available || variants[0];
  return pick ? getVariantFormValue(pick) : "";
}

/**
 * Bottom sheet for specific-products free-gift selection.
 */
export default function FreeGiftPickerSheet({
  open,
  onClose,
  cart,
  rewardSetting,
  trackingContext,
  formatMoney,
  onCartUpdated,
  embeddedInEditor = false,
  showVariantOutOfStockBadge = true,
  variantOutOfStockBadgeText = "Out of stock",
  variantOutOfStockBadgeStyle = null,
}) {
  const [tiersNeeding, setTiersNeeding] = useState([]);
  const [selectableProducts, setSelectableProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !rewardSetting || !isSpecificProductsRewardMode(rewardSetting)) {
      setTiersNeeding([]);
      setSelectableProducts([]);
      return undefined;
    }
    (async () => {
      try {
        await ensureRewardBarRuntimeContext(rewardSetting);
      } catch (_) {}
      if (cancelled) return;
      const nextTiers = getTiersForFreeGiftPicker(cart, rewardSetting);
      setTiersNeeding(nextTiers);
      const tier = nextTiers[0] || null;
      setSelectableProducts(
        tier ? getRewardTierSelectableProducts(tier) : []
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [open, cart, rewardSetting]);

  const activeTier = tiersNeeding[0] || null;
  const isChangeMode = activeTier?.pickerMode === "change";
  const selectLimit = Math.max(
    1,
    Number(activeTier?.products_to_offer_limit) || 1
  );
  const alreadySelected = useMemo(() => {
    if (!activeTier?.id) return 0;
    return (cart?.items || []).filter(
      (item) => getLineRewardTierId(item) === String(activeTier.id)
    ).length;
  }, [cart, activeTier]);

  // Change mode replaces the full gift set; select mode fills remaining slots.
  const remainingSlots = isChangeMode
    ? selectLimit
    : Math.max(0, selectLimit - alreadySelected);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [variantByProductId, setVariantByProductId] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectableProductKey = selectableProducts
    .map((product) => String(product?.id || ""))
    .join("|");

  useEffect(() => {
    if (!open || !activeTier) return;
    const nextVariants = {};
    for (const product of selectableProducts) {
      nextVariants[String(product.id)] = defaultVariantId(product);
    }

    const preselected = new Set();
    if (isChangeMode && activeTier?.id) {
      for (const item of cart?.items || []) {
        if (getLineRewardTierId(item) !== String(activeTier.id)) continue;
        const lineProductId = extractShopifyNumericId(
          item?.product_id || item?.productId || ""
        );
        const lineVariantId = String(item?.variant_id || "");
        const match = selectableProducts.find((product) => {
          const pid = extractShopifyNumericId(product?.id);
          if (pid && lineProductId && pid === lineProductId) return true;
          const variants = getProductVariants(product);
          return variants.some(
            (variant) => String(getVariantFormValue(variant)) === lineVariantId
          );
        });
        if (match?.id) {
          preselected.add(String(match.id));
          if (lineVariantId) {
            nextVariants[String(match.id)] = lineVariantId;
          }
        }
      }
    }

    setVariantByProductId(nextVariants);
    setSelectedIds(preselected);
    setError("");
    // selectableProducts identity changes each render; key tracks content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTier?.id, activeTier?.pickerMode, selectableProductKey]);

  useEffect(() => {
    if (!open || embeddedInEditor) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [embeddedInEditor, onClose, open]);

  const toggleProduct = useCallback(
    (productId) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const key = String(productId);
        if (next.has(key)) {
          next.delete(key);
          return next;
        }
        if (next.size >= remainingSlots) return prev;
        next.add(key);
        return next;
      });
    },
    [remainingSlots]
  );

  const handleConfirm = useCallback(async () => {
    if (!activeTier || embeddedInEditor || saving) return;
    if (!selectedIds.size) {
      setError("Select at least one free gift.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const selections = [];
      for (const product of selectableProducts) {
        if (!selectedIds.has(String(product.id))) continue;
        selections.push({
          tierId: activeTier.id,
          product,
          variantId: variantByProductId[String(product.id)] || defaultVariantId(product),
        });
      }
      const nextCart = await grantSelectedRewardGifts(
        cart,
        rewardSetting,
        selections,
        trackingContext,
        { replace: isChangeMode }
      );
      if (nextCart && typeof onCartUpdated === "function") {
        onCartUpdated(nextCart);
      }
      onClose?.();
    } catch (err) {
      setError(err?.message || "Could not add free gifts. Try again.");
    } finally {
      setSaving(false);
    }
  }, [
    activeTier,
    cart,
    embeddedInEditor,
    isChangeMode,
    onCartUpdated,
    onClose,
    rewardSetting,
    saving,
    selectableProducts,
    selectedIds,
    trackingContext,
    variantByProductId,
  ]);

  if (!open || !activeTier) return null;

  const selectedCount = selectedIds.size;
  const copy = resolvePickerCopy(rewardSetting || {});

  return (
    <div className="weu-custom-cart-drawer__sheet-root" role="presentation">
      <button
        type="button"
        className="weu-custom-cart-drawer__sheet-backdrop"
        onClick={embeddedInEditor ? undefined : onClose}
        aria-label="Close"
        tabIndex={embeddedInEditor ? -1 : 0}
      />
      <div
        className="weu-custom-cart-drawer__sheet-panel weu-custom-cart-drawer__sheet-panel--free-gift"
        role="dialog"
        aria-modal="true"
        aria-label={copy.title}
      >
        <div className="weu-custom-cart-drawer__sheet-header weu-custom-cart-drawer__sheet-header--free-gift">
          <div
            className="weu-custom-cart-drawer__sheet-header-art weu-custom-cart-drawer__sheet-header-art--gift"
            aria-hidden
          >
            <GiftHeaderIcon />
          </div>
          <div className="weu-custom-cart-drawer__sheet-heading">
            <p className="weu-custom-cart-drawer__free-gift-kicker">
              {copy.kicker}
            </p>
            <h3 className="weu-custom-cart-drawer__sheet-title">
              {copy.title}
            </h3>
            <p className="weu-custom-cart-drawer__sheet-subtitle">
              {copy.subtitle}
            </p>
          </div>
          <button
            type="button"
            className="weu-custom-cart-drawer__sheet-close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="weu-custom-cart-drawer__sheet-body">
          <ul className="weu-custom-cart-drawer__free-gift-list">
            {selectableProducts.map((product) => {
              const productId = String(product.id);
              const checked = selectedIds.has(productId);
              const variants = getProductVariants(product);
              const variantId =
                variantByProductId[productId] || defaultVariantId(product);
              const priceCents = productPriceCents(product, variantId);
              const disabled =
                !checked && selectedCount >= remainingSlots;
              return (
                <li
                  key={productId}
                  className={`weu-custom-cart-drawer__free-gift-row${
                    checked
                      ? " weu-custom-cart-drawer__free-gift-row--selected"
                      : ""
                  }${
                    disabled
                      ? " weu-custom-cart-drawer__free-gift-row--disabled"
                      : ""
                  }`}
                >
                  <button
                    type="button"
                    className="weu-custom-cart-drawer__free-gift-row-main"
                    onClick={() => !disabled && toggleProduct(productId)}
                    disabled={disabled && !checked}
                  >
                    <span className="weu-custom-cart-drawer__free-gift-thumb-wrap">
                      {productImage(product) ? (
                        <img
                          src={productImage(product)}
                          alt=""
                          className="weu-custom-cart-drawer__free-gift-thumb"
                        />
                      ) : (
                        <span className="weu-custom-cart-drawer__free-gift-thumb weu-custom-cart-drawer__free-gift-thumb--empty" />
                      )}
                      <span className="weu-custom-cart-drawer__free-gift-qty-badge">
                        1
                      </span>
                    </span>
                    <span className="weu-custom-cart-drawer__free-gift-meta">
                      <span className="weu-custom-cart-drawer__free-gift-title">
                        {product.title || "Gift"}
                      </span>
                      <span className="weu-custom-cart-drawer__free-gift-price">
                        <span className="weu-custom-cart-drawer__free-gift-price-free">
                          {copy.freeLabel}
                        </span>
                        {priceCents > 0 && typeof formatMoney === "function" ? (
                          <s className="weu-custom-cart-drawer__free-gift-price-was">
                            {formatMoney(priceCents)}
                          </s>
                        ) : null}
                      </span>
                    </span>
                  </button>
                  {variants.length > 1 ? (
                    <CartDrawerVariantSelect
                      options={variants.map((variant) => ({
                        id: getVariantFormValue(variant),
                        label: variant.title || variant.name || "Variant",
                        available: isVariantAvailable(variant),
                      }))}
                      value={variantId}
                      showOutOfStockBadge={showVariantOutOfStockBadge}
                      outOfStockBadgeText={variantOutOfStockBadgeText}
                      outOfStockBadgeStyle={variantOutOfStockBadgeStyle}
                      onChange={(nextId) =>
                        setVariantByProductId((prev) => ({
                          ...prev,
                          [productId]: nextId,
                        }))
                      }
                    />
                  ) : null}
                  <button
                    type="button"
                    className={`weu-custom-cart-drawer__free-gift-check${
                      checked
                        ? " weu-custom-cart-drawer__free-gift-check--on"
                        : ""
                    }`}
                    aria-pressed={checked}
                    aria-label={
                      checked
                        ? `Deselect ${product.title}`
                        : `Select ${product.title}`
                    }
                    disabled={disabled && !checked}
                    onClick={() => toggleProduct(productId)}
                  >
                    {checked ? "✓" : ""}
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="weu-custom-cart-drawer__free-gift-counter">
            {applyCounterTemplate(
              copy.counterLabel,
              selectedCount,
              remainingSlots
            )}
          </p>
        </div>

        <div className="weu-custom-cart-drawer__sheet-footer weu-custom-cart-drawer__free-gift-footer">
          <button
            type="button"
            className="weu-custom-cart-drawer__sheet-save"
            onClick={handleConfirm}
            disabled={embeddedInEditor || saving || selectedCount < 1}
          >
            {saving
              ? "…"
              : applyCountTemplate(copy.confirmLabel, selectedCount)}
          </button>
          <button
            type="button"
            className="weu-custom-cart-drawer__free-gift-continue"
            onClick={onClose}
          >
            {copy.continueLabel}
          </button>
        </div>
        {error ? (
          <p className="weu-custom-cart-drawer__sheet-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Auto-open controller for free-gift picker when a specific-products tier unlocks.
 * After dismiss / after pick, shoppers reopen via the free-gift banner (canReopen)
 * to finish selecting or to change their gift.
 */
export function useFreeGiftPickerController({
  cart,
  rewardSetting,
  enabled = true,
}) {
  const [open, setOpen] = useState(false);
  const [needingTiers, setNeedingTiers] = useState([]);
  const [changeableTiers, setChangeableTiers] = useState([]);
  const promptedRef = useRef(new Set());
  const prevNeedingRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    if (!enabled || !rewardSetting || !isSpecificProductsRewardMode(rewardSetting)) {
      setNeedingTiers([]);
      setChangeableTiers([]);
      return undefined;
    }
    (async () => {
      try {
        await ensureRewardBarRuntimeContext(rewardSetting);
      } catch (_) {}
      if (cancelled) return;
      setNeedingTiers(getTiersNeedingGiftSelection(cart, rewardSetting));
      setChangeableTiers(getTiersAllowingGiftChange(cart, rewardSetting));
    })();
    return () => {
      cancelled = true;
    };
  }, [cart, enabled, rewardSetting]);

  useEffect(() => {
    if (!enabled || !rewardSetting || !isSpecificProductsRewardMode(rewardSetting)) {
      setOpen(false);
      prevNeedingRef.current = new Set();
      return;
    }

    const needingIds = new Set(needingTiers.map((tier) => String(tier.id)));

    // Clear prompt memory for tiers that dropped out of "needing" (unmatched or fulfilled).
    for (const id of [...promptedRef.current]) {
      if (!needingIds.has(id)) {
        promptedRef.current.delete(id);
      }
    }

    const newlyNeeding = needingTiers.filter(
      (tier) => !prevNeedingRef.current.has(String(tier.id))
    );
    prevNeedingRef.current = needingIds;

    if (!newlyNeeding.length) return;

    const toPrompt = newlyNeeding.find(
      (tier) => !promptedRef.current.has(String(tier.id))
    );
    if (!toPrompt) return;

    promptedRef.current.add(String(toPrompt.id));
    setOpen(true);
  }, [enabled, needingTiers, rewardSetting]);

  const canReopen = Boolean(
    enabled &&
      !open &&
      (needingTiers.length > 0 || changeableTiers.length > 0) &&
      isSpecificProductsRewardMode(rewardSetting)
  );

  return {
    open,
    setOpen,
    close: () => setOpen(false),
    reopen: () => setOpen(true),
    canReopen,
    needingTierCount: needingTiers.length,
    canChangeGift: changeableTiers.length > 0,
  };
}
